import React, { useEffect, useCallback } from 'react';
import { ArrowLeftToLine, ArrowRightToLine } from 'lucide-react';
import { dropStagePrzesunZadanieGold } from './DragDrop/dropStagePrzesunZadanieGold';
import { dropStagePrzesunZadanieOprawaGold } from './DragDrop/dropStagePrzesunZadanieOprawaGold';

const formatDateToCustomString = (dateObj) => {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

// Przy skoku w lewo przez zablokowany klocek: jeśli docelowe miejsce zahacza
// o kolejny zablokowany klocek, cofamy start przed początek tej blokady
const adjustLeftStartForLocked = (proposedStartMs, durationMs, lockedStages) => {
  let startMs = proposedStartMs;
  let changed = true;
  let safety = 0;

  while (changed && safety < 50) {
    changed = false;
    safety++;

    for (let i = lockedStages.length - 1; i >= 0; i--) {
      const locked = lockedStages[i];
      if (locked._sMs < startMs + durationMs && locked._eMs > startMs) {
        startMs = locked._sMs - durationMs;
        changed = true;
      }
    }
  }

  return startMs;
};

const executeStageMove = ({
  procesNazwaId,
  draggedId,
  targetId,
  procesorId,
  dropDate,
  refreshGantAll,
}) => {
  if (procesNazwaId == 6) {
    dropStagePrzesunZadanieOprawaGold(
      draggedId,
      targetId,
      procesorId,
      dropDate,
      refreshGantAll
    );
  } else {
    dropStagePrzesunZadanieGold(
      draggedId,
      targetId,
      procesorId,
      dropDate,
      refreshGantAll
    );
  }
};

export const useGanttSnapNavigation = ({
  selectedGrup,
  groupedStages,
  moveable,
  canDragOnGant,
  refreshGantAll,
}) => {
  const snapOrSwapNeighbor = useCallback(
    (direction) => {
      if (!selectedGrup || !groupedStages) return;

      const selectedId = String(selectedGrup.global_id || selectedGrup.id);
      const processorStages = groupedStages[selectedGrup.procesor_id] || [];

      // Pobieramy zawsze świeży obiekt klocka z aktualnych danych wykresu
      const currentStage = processorStages.find(
        (s) => String(s.global_id || s.id) === selectedId
      );

      if (!currentStage) return;

      // Sprawdzamy, czy zaznaczony klocek można przesuwać
      if (
        !moveable ||
        (canDragOnGant && !canDragOnGant(currentStage.proces_nazwa_id)) ||
        currentStage.zablokowany == 1
      ) {
        return;
      }

      const curStartMs = new Date(currentStage.start).getTime();
      const curEndMs = new Date(currentStage.end).getTime();
      const durationMs = curEndMs - curStartMs;

      if (Number.isNaN(curStartMs) || Number.isNaN(curEndMs) || durationMs <= 0) {
        return;
      }

      // 1 minuta tolerancji na zaokrąglenia minutowe z bazy
      const TOLERANCE_MS = 60 * 1000;

      // Wszystkie pozostałe klocki na tym procesorze (zarówno odblokowane, jak i zablokowane!)
      const allOtherParsed = processorStages
        .filter((s) => String(s.global_id || s.id) !== selectedId)
        .map((s) => ({
          ...s,
          _sMs: new Date(s.start).getTime(),
          _eMs: new Date(s.end).getTime(),
        }))
        .filter((s) => !Number.isNaN(s._sMs) && !Number.isNaN(s._eMs));

      if (allOtherParsed.length === 0) return;

      const lockedStages = allOtherParsed.filter((s) => s.zablokowany == 1);

      if (direction === 'left') {
        // Szukamy najbliższego sąsiada po lewej (sortujemy po końcu rosnąco)
        const leftNeighbors = allOtherParsed
          .filter((s) => s._sMs < curStartMs - TOLERANCE_MS)
          .sort((a, b) => a._eMs - b._eMs || a._sMs - b._sMs);

        if (leftNeighbors.length === 0) return;

        const prevStage = leftNeighbors[leftNeighbors.length - 1];

        // 1. Jest przerwa między końcem sąsiada a początkiem naszego klocka -> PRZYKLEJ do sąsiada
        if (curStartMs > prevStage._eMs + TOLERANCE_MS) {
          const newStartDateStr = formatDateToCustomString(new Date(prevStage._eMs));

          executeStageMove({
            procesNazwaId: currentStage.proces_nazwa_id,
            draggedId: currentStage.global_id,
            targetId: null,
            procesorId: currentStage.procesor_id,
            dropDate: newStartDateStr,
            refreshGantAll,
          });
        } else {
          // 2. Już się stykają -> PRZESKOCZ W LEWO
          if (prevStage.zablokowany == 1) {
            // Jeśli sąsiad jest ZABLOKOWANY -> wskakujemy przed niego (na lewy bok blokady)
            const jumpLeftLockedMs = adjustLeftStartForLocked(
              prevStage._sMs - durationMs,
              durationMs,
              lockedStages
            );
            const newStartDateStr = formatDateToCustomString(new Date(jumpLeftLockedMs));

            executeStageMove({
              procesNazwaId: currentStage.proces_nazwa_id,
              draggedId: currentStage.global_id,
              targetId: null,
              procesorId: currentStage.procesor_id,
              dropDate: newStartDateStr,
              refreshGantAll,
            });
          } else {
            // Jeśli sąsiad jest ODBLOKOWANY -> wskakujemy na jego początek, a petla_domino spycha sąsiada za nas
            const newStartDateStr = formatDateToCustomString(new Date(prevStage._sMs));

            executeStageMove({
              procesNazwaId: currentStage.proces_nazwa_id,
              draggedId: currentStage.global_id,
              targetId: null,
              procesorId: currentStage.procesor_id,
              dropDate: newStartDateStr,
              refreshGantAll,
            });
          }
        }
      } else if (direction === 'right') {
        // Szukamy najbliższego sąsiada po prawej (sortujemy po starcie rosnąco)
        const rightNeighbors = allOtherParsed
          .filter((s) => s._sMs > curStartMs + TOLERANCE_MS)
          .sort((a, b) => a._sMs - b._sMs || a._eMs - b._eMs);

        if (rightNeighbors.length === 0) return;

        const nextStage = rightNeighbors[0];

        // 1. Jest przerwa między końcem naszego klocka a początkiem sąsiada -> PRZYKLEJ do sąsiada
        if (curEndMs < nextStage._sMs - TOLERANCE_MS) {
          const targetStartMs = nextStage._sMs - durationMs;
          const newStartDateStr = formatDateToCustomString(new Date(targetStartMs));

          executeStageMove({
            procesNazwaId: currentStage.proces_nazwa_id,
            draggedId: currentStage.global_id,
            targetId: null,
            procesorId: currentStage.procesor_id,
            dropDate: newStartDateStr,
            refreshGantAll,
          });
        } else {
          // 2. Już się stykają -> PRZESKOCZ W PRAWO
          if (nextStage.zablokowany == 1) {
            // Jeśli sąsiad jest ZABLOKOWANY -> nasz klocek przeskakuje na koniec zablokowanego sąsiada
            executeStageMove({
              procesNazwaId: currentStage.proces_nazwa_id,
              draggedId: currentStage.global_id,
              targetId: nextStage.global_id,
              procesorId: currentStage.procesor_id,
              dropDate: null,
              refreshGantAll,
            });
          } else {
            // Jeśli sąsiad jest ODBLOKOWANY -> przesuwamy sąsiada na początek naszego klocka,
            // dzięki czemu petla_domino w SQL wypchnie nasz klocek dokładnie za niego bez tworzenia luki!
            const swapStartDateStr = formatDateToCustomString(new Date(curStartMs));

            executeStageMove({
              procesNazwaId: nextStage.proces_nazwa_id,
              draggedId: nextStage.global_id,
              targetId: null,
              procesorId: currentStage.procesor_id,
              dropDate: swapStartDateStr,
              refreshGantAll,
            });
          }
        }
      }
    },
    [selectedGrup, groupedStages, moveable, canDragOnGant, refreshGantAll]
  );

  // Nasłuchiwanie strzałek Lewo / Prawo na klawiaturze
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedGrup) return;

      const activeTag = document.activeElement?.tagName;
      if (activeTag === 'INPUT' || activeTag === 'TEXTAREA' || activeTag === 'SELECT') {
        return;
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        snapOrSwapNeighbor('left');
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        snapOrSwapNeighbor('right');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedGrup, snapOrSwapNeighbor]);

  return {
    snapLeft: () => snapOrSwapNeighbor('left'),
    snapRight: () => snapOrSwapNeighbor('right'),
    canSnap: Boolean(selectedGrup && moveable && selectedGrup.zablokowany != 1),
  };
};

export const GanttSnapButtons = ({ snapNav, buttonClassName = '' }) => {
  if (!snapNav) return null;

  return (
    <>
      <button
        className={buttonClassName}
        onClick={snapNav.snapLeft}
        disabled={!snapNav.canSnap}
        style={{
          backgroundColor: snapNav.canSnap ? '#19675e' : '#63636396',
          cursor: snapNav.canSnap ? 'pointer' : 'not-allowed',
        }}
        title="Przyklej do sąsiada po lewej / Przeskocz w lewo (Strzałka w lewo)"
      >
        <ArrowLeftToLine size={20} style={{ marginRight: '0px' }} />
      </button>

      <button
        className={buttonClassName}
        onClick={snapNav.snapRight}
        disabled={!snapNav.canSnap}
        style={{
          backgroundColor: snapNav.canSnap ? '#19675e' : '#63636396',
          cursor: snapNav.canSnap ? 'pointer' : 'not-allowed',
        }}
        title="Przyklej do sąsiada po prawej / Przeskocz w prawo (Strzałka w prawo)"
      >
        <ArrowRightToLine size={20} style={{ marginRight: '0px' }} />
      </button>
    </>
  );
};